import React, { useState, useEffect } from 'react';
import { Button, Col, Input, Row, Label, ModalBody, Form, Spinner, FormFeedback, Card, CardBody, Accordion, AccordionItem, Collapse } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { setRequireCloseModal } from '../../../../../slices/screen/reducer';
import OSelect from '../../../../../Components/OElements/OSelect';
import { addNewReportTemplate, getGraphList, getPageList, updateReportTemplate } from '../../../../../slices/thunks';
import PageComponents from '../../Components/Graph/PageComponents';

import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import classnames from 'classnames';

const ItemType = {
	ACCORDION_ITEM: 'accordionItem',
};

const DraggableAccordionItem = ({ item, index, graphs, moveAccordionItem, removeAccordionItem, isOpen, toggleAccordion }) => {
	const ref = React.useRef(null);

	const [, drop] = useDrop({
		accept: ItemType.ACCORDION_ITEM,
		hover(draggedItem) {
			if (draggedItem.index !== index) {
				moveAccordionItem(draggedItem.index, index);
				draggedItem.index = index;
			}
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: ItemType.ACCORDION_ITEM,
		item: { index },
		collect: monitor => ({
			isDragging: monitor.isDragging(),
		}),
	});

	drag(drop(ref));

	return (
		<div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>

				<h2 className="accordion-header" id={`accordionborderedExample${index}`}>
					<button
						className={classnames("accordion-button", { collapsed: isOpen !== index })}
						type="button"
						onClick={() => toggleAccordion(index)}
						style={{ cursor: "pointer" }}
					>
						Nombre de la página: {item.item.name}
					</button>
				</h2>
				<Collapse isOpen={isOpen === index} className="accordion-collapse">
					<div className="accordion-body">
						<Row>
							<Col md={11}>
								{item.item.elements.map((element, idx) => {
									const matchingGraph = element.graph ? graphs.find(graph => graph._id === element.graph) : null;
									const SelectedGraphComponent = matchingGraph && matchingGraph.code ? PageComponents[matchingGraph.code] : null;

									return (
										<div key={idx}>
											{element.title && <p>Título: {element.title}</p>}
											{element.text && <p>Texto: {element.text}</p>}
											{matchingGraph && (
												<div style={{ height: '300px' }}>
													<React.Suspense fallback={<div>Loading...</div>}>
														<SelectedGraphComponent />
													</React.Suspense>
												</div>
											)}
										</div>
									);
								})}
							</Col>
							<Col md={1}>
								<button type="button" className="btn btn-sm btn-danger" onClick={() => removeAccordionItem(item)}>
									<i className="ri-delete-bin-line"></i>
								</button>
							</Col>
						</Row>
					</div>
				</Collapse>
		</div>
	);
};

const ReportTemplateForm = ({ dataReportTemplate, isEdit, handleToggleModal, isOpen }) => {
	const dispatch = useDispatch();
	const selectorReportTemplate = createSelector(
		state => state.Screen,
		data => ({
			pages: data.pageList,
			graphs: data.graphList,
			isAdding: data.isAdding,
			isAdded: data.isAdded,
			isUpdating: data.isUpdating,
			isUpdated: data.isUpdated,
			isLoadingList: data.isLoadingList,
			requireCloseModal: data.requireCloseModal,
		})
	);

	const { isAdding, pages, graphs, isUpdating, requireCloseModal } = useSelector(selectorReportTemplate);
	const [pageList, setPageList] = useState([]);
	const [selectedPage, setSelectedPage] = useState(null);
	const [accordionItems, setAccordionItems] = useState([]);
	const [openAccordion, setOpenAccordion] = useState(null);

	// Validation
	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			name: dataReportTemplate?.name || '',
			description: dataReportTemplate?.description || '',
			page_id: null,
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Campo requerido'),
			description: Yup.string().required('Campo requerido'),
			page_id: Yup.string().nullable(),
		}),
		validateOnChange: true,
		onSubmit: async values => {
			try {
				const data_report = {
					...values,
					...(isEdit && { _id: dataReportTemplate._id }),
				};
				data_report['pages'] = accordionItems.map(element => element.item);
				
				if (isEdit) {
					await dispatch(updateReportTemplate(data_report));
				} else {
					await dispatch(addNewReportTemplate(data_report));
				}
			} finally {
				dispatch(setRequireCloseModal(true));
			}
		},
	});

	const moveAccordionItem = (fromIndex, toIndex) => {
		const updatedAccordionItems = update(accordionItems, {
			$splice: [
				[fromIndex, 1],
				[toIndex, 0, accordionItems[fromIndex]],
			],
		});
		setAccordionItems(updatedAccordionItems);
	};

	const handlePageSelected = optionSelected => {
		setSelectedPage(optionSelected);
	};

	const handleAddPageToAccordion = () => {
		if (selectedPage) {
			const notSelectedPages = pageList.filter(page => page.value !== selectedPage._id);
			const selectedItem = pageList.find(page => page.value === selectedPage._id);
			const updatedAccordionItems = [...accordionItems, selectedItem];
			setAccordionItems(updatedAccordionItems);
			setPageList(notSelectedPages);
			setSelectedPage(null);
		}
	};

	const handleRemoveAccordionItem = itemToRemove => {
		const updatedAccordionItems = accordionItems.filter(item => item.value !== itemToRemove.value);
		setAccordionItems(updatedAccordionItems);
		setPageList(prevPages => [...prevPages, itemToRemove]);
	};

	const toggleAccordion = id => {
		setOpenAccordion(openAccordion === id ? null : id);
	};

	useEffect(() => {
		dispatch(getPageList());
		dispatch(getGraphList());
	}, [dispatch]);

	useEffect(() => {
		if (pages && pages.length > 0) {
			const formattedPages = pages.map(page => ({
				value: page._id,
				label: page.name,
				item: {
					...page,
					page_id: page._id,
				},
			}));
			setPageList(formattedPages);
		}
	}, [pages]);


	useEffect(() => {
		if (isOpen) {
			if (requireCloseModal) {
				dispatch(setRequireCloseModal(false));
				handleToggleModal();
			}
		}
	}, [requireCloseModal, dispatch, handleToggleModal, isOpen]);

	console.log(pageList)

	return (
			<Form
				className='tablelist-form'
				onSubmit={e => {
					e.preventDefault();
					validation.handleSubmit();
					return false;
				}}
			>
				<ModalBody className='modal-body'>
					<Row className='g-3'>
						<Col lg={12}>
							<Label for='name' className='form-label'>
								Nombre del reporte
							</Label>
							<Input
								name='name'
								id='name'
								className='form-control'
								placeholder='Ingrese el nombre del reporte'
								type='text'
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={validation.values.name || ''}
								invalid={validation.touched.name && validation.errors.name ? true : false}
							/>
							{validation.touched.name && validation.errors.name ? (
								<FormFeedback type='invalid'>{validation.errors.name}</FormFeedback>
							) : null}
						</Col>
						<Col lg={12}>
							<Label for='description' className='form-label'>
								Descripción
							</Label>
							<Input
								name='description'
								id='description'
								className='form-control'
								placeholder='Ingrese el nombre del reporte'
								type='textarea'
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={validation.values.description || ''}
								invalid={validation.touched.description && validation.errors.description ? true : false}
							/>
							{validation.touched.description && validation.errors.description ? (
								<FormFeedback type='invalid'>{validation.errors.description}</FormFeedback>
							) : null}
						</Col>
						<Col md={3}>
							{pageList.length > 0 ? (
								<OSelect
									key={'page_id'}
									label='Página'
									id='page_id'
									name='page_id'
									value={selectedPage}
									onSelect={handlePageSelected}
									itemsSelect={pageList}
									validation={validation}
								/>
							) : null}

							{pageList.length > 0 ? (
								<button
									type='button'
									className='btn btn-label btn-primary mt-2'
									id='add-btn'
									onClick={handleAddPageToAccordion}
									disabled={!selectedPage}
								>
									<i className={`${Icons.plus} label-icon`}></i> Incorporar página
								</button>
							) : null}
						</Col>
						<Col>
							<Card className='mt-2'>
								<CardBody>
									<Accordion
										className="custom-accordionwithicon custom-accordion-border accordion-border-box accordion-success"
										id="accordionBordered"
									>
										{accordionItems.length > 0 ? (
											accordionItems.map((page, index) => (
												<DraggableAccordionItem
													key={page.value}
													item={page}
													graphs={graphs}
													index={index}
													moveAccordionItem={moveAccordionItem}
													removeAccordionItem={handleRemoveAccordionItem}
													isOpen={openAccordion}
													toggleAccordion={toggleAccordion}
												/>
											))
										) : (
											<p>No hay páginas</p>
										)}
									</Accordion>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</ModalBody>
				<div className='modal-footer'>
					<div className='hstack gap-2 justify-content-end'>
						<Button
							type='button'
							onClick={() => {
								handleToggleModal();
							}} className='btn-danger btn-label'>
							<i className="icon-x label-icon"></i>
							Cancelar
						</Button>
						<button
							type='submit'
							className='btn btn-label btn-primary'
							id='add-btn'
							disabled={isAdding || isUpdating}
						>
							{isAdding || isUpdating ? (
								<i className='label-icon'><Spinner size='sm' /></i>
							) : <i className="ri-save-3-line label-icon"></i>}
							{isEdit ? 'Editar' : 'Crear'} Plantilla
						</button>
					</div>
				</div>
			</Form>
	);
};

export default ReportTemplateForm;
